import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, BookOpen, CheckCircle2, KeyRound, LogOut, MailCheck, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAccount } from "@/lib/AccountContext";
import { useLang } from "@/lib/i18n";
import useSEO from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AccountDemographicFields, AccountNameField } from "@/components/AccountProfileFields";
import { profileFormValues, profilePayload } from "@/lib/accountProfile";

export function AccountSettingsContent({
  user,
  t,
  lang,
  onRequestVerification,
  onLogout,
  onDelete,
  onSaveProfile,
}) {
  const [profile, setProfile] = useState(() => profileFormValues(user.profile));
  const [profileState, setProfileState] = useState("");
  const [verificationState, setVerificationState] = useState("");
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const requestVerification = async () => {
    setVerificationState("working");
    try {
      await onRequestVerification(lang);
      setVerificationState("sent");
    } catch (error) {
      setVerificationState(error?.response?.status === 429 ? "rate_limit" : "error");
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setProfileState("working");
    try {
      const saved = await onSaveProfile(profilePayload(profile));
      setProfile(profileFormValues(saved?.profile));
      setProfileState("saved");
    } catch {
      setProfileState("error");
    }
  };

  const deleteAccount = async (event) => {
    event.preventDefault();
    setDeleteError("");
    if (!confirmed || !password) return;
    setDeleting(true);
    try {
      await onDelete(password);
    } catch (error) {
      setDeleteError(error?.response?.status === 429
        ? t("account.recovery.error.rate_limit")
        : t("account.settings.delete_error"));
      setDeleting(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {onSaveProfile && (
        <section className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-7 lg:col-span-2" data-testid="settings-profile">
          <UserRound className="h-6 w-6 text-teal-700" />
          <h2 className="mt-4 font-display text-2xl">{t("account.profile.title")}</h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">{t("account.profile.description")}</p>
          <form onSubmit={saveProfile} className="mt-6 grid max-w-3xl gap-4 sm:grid-cols-2" noValidate>
            <div className="sm:col-span-2">
              <AccountNameField value={profile.name} onChange={(name) => { setProfile({ ...profile, name }); setProfileState(""); }} t={t} idPrefix="settings" />
            </div>
            <div className="sm:col-span-2">
              <AccountDemographicFields profile={profile} onChange={(next) => { setProfile(next); setProfileState(""); }} t={t} lang={lang} idPrefix="settings" />
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
              <Button type="submit" disabled={profileState === "working"} className="rounded-full" data-testid="settings-save-profile">
                {profileState === "working" ? t("account.working") : t("account.profile.save")}
              </Button>
              {(profileState === "saved" || profileState === "error") && (
                <p role={profileState === "saved" ? "status" : "alert"} className={`text-sm ${profileState === "error" ? "text-destructive" : "text-muted-foreground"}`} data-testid="settings-profile-status">
                  {t(profileState === "saved" ? "account.profile.saved" : "account.profile.save_error")}
                </p>
              )}
            </div>
          </form>
        </section>
      )}

      <section className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-7">
        <MailCheck className="h-6 w-6 text-teal-700" />
        <h2 className="mt-4 font-display text-2xl">{t("account.settings.email_title")}</h2>
        <p className="mt-2 break-all text-sm font-medium text-teal-900">{user.email}</p>
        <div className="mt-4 flex items-center gap-2 text-sm">
          {user.email_verified ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
          {t(user.email_verified ? "account.settings.verified" : "account.settings.unverified")}
        </div>
        {!user.email_verified && (
          <Button type="button" variant="outline" onClick={requestVerification} disabled={verificationState === "working" || verificationState === "sent"} className="mt-5 rounded-full" data-testid="settings-send-verification">
            {t(verificationState === "working" ? "account.working" : "account.settings.send_verification")}
          </Button>
        )}
        {verificationState && verificationState !== "working" && (
          <p role={verificationState === "sent" ? "status" : "alert"} className="mt-3 text-sm text-muted-foreground" data-testid="settings-verification-status">
            {t(verificationState === "sent" ? "account.settings.verification_sent" : verificationState === "rate_limit" ? "account.recovery.error.rate_limit" : "account.error.network")}
          </p>
        )}
      </section>

      <section className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-7">
        <KeyRound className="h-6 w-6 text-teal-700" />
        <h2 className="mt-4 font-display text-2xl">{t("account.settings.security_title")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t("account.settings.security_description")}</p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <Button asChild variant="outline" className="rounded-full"><Link to="/account/forgot-password" state={{ email: user.email }}>{t("account.settings.reset_password")}</Link></Button>
          <Button type="button" variant="ghost" onClick={onLogout} className="justify-start rounded-full"><LogOut className="h-4 w-4" /> {t("account.signout")}</Button>
        </div>
      </section>

      <section className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-7 lg:col-span-2">
        <BookOpen className="h-6 w-6 text-teal-700" />
        <h2 className="mt-4 font-display text-2xl">{t("account.settings.saved_title")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t("account.settings.saved_description")}</p>
        <Button asChild variant="outline" className="mt-5 rounded-full"><Link to="/account/results">{t("account.settings.open_results")}</Link></Button>
      </section>

      <section className="rounded-3xl border border-destructive/30 bg-card p-6 shadow-sm sm:p-7 lg:col-span-2">
        <Trash2 className="h-6 w-6 text-destructive" />
        <h2 className="mt-4 font-display text-2xl">{t("account.settings.delete_title")}</h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">{t("account.settings.delete_description")}</p>
        <form onSubmit={deleteAccount} className="mt-6 max-w-xl space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="delete-account-password">{t("account.settings.current_password")}</Label>
            <Input id="delete-account-password" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} className="h-12" />
          </div>
          <div className="flex items-start gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-4">
            <Checkbox id="delete-account-confirmation" checked={confirmed} onCheckedChange={(checked) => setConfirmed(checked === true)} />
            <Label htmlFor="delete-account-confirmation" className="cursor-pointer text-sm font-normal leading-relaxed">{t("account.settings.delete_confirm")}</Label>
          </div>
          {deleteError && <div role="alert" className="text-sm text-destructive">{deleteError}</div>}
          <Button type="submit" variant="destructive" disabled={!confirmed || !password || deleting} aria-busy={deleting} className="rounded-full" data-testid="settings-delete-account">
            {deleting ? t("account.working") : t("account.settings.delete_action")}
          </Button>
        </form>
      </section>
    </div>
  );
}

export default function AccountSettings() {
  const { user, requestEmailVerification, logout, deleteAccount, updateProfile } = useAccount();
  const { lang, t } = useLang();
  const navigate = useNavigate();

  useSEO({ title: `${t("account.settings.title")} — Mindovia`, description: t("account.settings.description"), url: null, robots: "noindex, nofollow, noarchive" });

  const handleLogout = async () => {
    await logout();
    toast.success(t("account.signed_out"));
    navigate("/", { replace: true });
  };
  const handleDelete = async (password) => {
    await deleteAccount(password);
    toast.success(t("account.settings.deleted"));
    navigate("/", { replace: true });
  };

  return (
    <main className="mx-auto min-h-[68vh] max-w-5xl px-4 py-10 sm:px-6 sm:py-14" data-wrap="responsive">
      <h1 className="font-display text-4xl tracking-tight sm:text-5xl">{t("account.settings.title")}</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t("account.settings.description")}</p>
      <div className="mt-8">
        <AccountSettingsContent user={user} t={t} lang={lang} onRequestVerification={requestEmailVerification} onLogout={handleLogout} onDelete={handleDelete} onSaveProfile={updateProfile} />
      </div>
    </main>
  );
}
